import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./DateRangePicker.css";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1);

const isSameDay = (a, b) => Boolean(a && b && a.toDateString() === b.toDateString());
const isBeforeDay = (a, b) => startOfDay(a).getTime() < startOfDay(b).getTime();
const isAfterDay = (a, b) => startOfDay(a).getTime() > startOfDay(b).getTime();

const formatMonthLabel = (date) =>
  date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

const pad2 = (value) => String(value).padStart(2, "0");
const toIso = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const nightsBetween = (start, end) => {
  if (!start || !end) return 0;
  const diff = startOfDay(end).getTime() - startOfDay(start).getTime();
  return Math.max(0, Math.round(diff / 86400000));
};

const buildCells = (monthStart) => {
  const firstDay = monthStart.getDay();
  const totalDays = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    0
  ).getDate();

  const cells = [];
  for (let idx = 0; idx < firstDay; idx += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= totalDays; day += 1) {
    cells.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), day));
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
};

export default function DateRangePicker({
  open,
  checkIn,
  checkOut,
  anchorRect,
  onChange,
  onClose,
}) {
  const popoverRef = useRef(null);
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [activeField, setActiveField] = useState("checkIn");
  const [hoverDate, setHoverDate] = useState(null);
  const [positionStyle, setPositionStyle] = useState({});

  const today = useMemo(() => startOfDay(new Date()), []);
  const monthA = useMemo(() => startOfMonth(currentMonth), [currentMonth]);
  const monthB = useMemo(() => addMonths(monthA, 1), [monthA]);
  const monthACells = useMemo(() => buildCells(monthA), [monthA]);
  const monthBCells = useMemo(() => buildCells(monthB), [monthB]);
  const totalNights = useMemo(() => nightsBetween(checkIn, checkOut), [checkIn, checkOut]);

  useEffect(() => {
    if (!open) return;

    if (checkIn && !checkOut) {
      setActiveField("checkOut");
    } else {
      setActiveField("checkIn");
    }

    const el = popoverRef.current;
    if (!el || !anchorRect) return;

    const frame = window.requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const nextStyle = {
        top: `${anchorRect.bottom + 8}px`,
        left: `${anchorRect.left}px`,
      };

      if (rect.right > window.innerWidth - 16) {
        nextStyle.right = "16px";
        nextStyle.left = "auto";
      }

      if (rect.bottom > window.innerHeight - 16) {
        nextStyle.bottom = "16px";
        nextStyle.top = "auto";
      }

      setPositionStyle(nextStyle);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [open, checkIn, checkOut, anchorRect]);

  useEffect(() => {
    if (!open) return;
    setHoverDate(null);
  }, [open]);

  const isDisabled = (day) => isBeforeDay(day, today);

  const inFinalRange = (day) =>
    Boolean(checkIn && checkOut && isAfterDay(day, checkIn) && isBeforeDay(day, checkOut));

  const inHoverRange = (day) => {
    if (!checkIn || checkOut || !hoverDate) return false;
    if (!isAfterDay(hoverDate, checkIn)) return false;
    return isAfterDay(day, checkIn) && isBeforeDay(day, hoverDate);
  };

  const handleDayClick = (day) => {
    if (isDisabled(day)) return;

    if (!checkIn || (checkIn && checkOut)) {
      onChange?.({ checkIn: day, checkOut: null });
      setActiveField("checkOut");
      return;
    }

    if (isAfterDay(day, checkIn)) {
      onChange?.({ checkIn, checkOut: day });
      window.setTimeout(() => {
        onClose?.();
      }, 200);
      return;
    }

    onChange?.({ checkIn: day, checkOut: null });
    setActiveField("checkOut");
  };

  const renderMonth = (monthDate, cells) => {
    return (
      <section className="hv-cal-month" key={monthDate.toISOString()}>
        <h4>{formatMonthLabel(monthDate)}</h4>
        <div className="hv-cal-weekdays">
          {WEEKDAYS.map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="hv-cal-grid">
          {cells.map((day, idx) => {
            if (!day) return <span key={`empty-${idx}`} className="hv-cal-empty" />;

            const disabled = isDisabled(day);
            const isStart = isSameDay(day, checkIn);
            const isEnd = isSameDay(day, checkOut);
            const isToday = isSameDay(day, today);
            const inRange = inFinalRange(day) || inHoverRange(day);

            const classes = ["hv-cal-day"];
            if (disabled) classes.push("is-disabled");
            if (inRange) classes.push("is-in-range");
            if (isStart) classes.push("is-start");
            if (isEnd) classes.push("is-end");
            if (isToday) classes.push("is-today");

            return (
              <button
                type="button"
                key={day.toISOString()}
                className={classes.join(" ")}
                onClick={() => handleDayClick(day)}
                onMouseEnter={() => {
                  if (checkIn && !checkOut) {
                    setHoverDate(day);
                  }
                }}
                onMouseLeave={() => {
                  if (checkIn && !checkOut) {
                    setHoverDate(null);
                  }
                }}
                disabled={disabled}
              >
                <span>{day.getDate()}</span>
                {isToday ? <i className="hv-cal-today-dot" /> : null}
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  if (!open) return null;

  const content = (
    <div className="hv-cal-overlay" onClick={onClose}>
      <div
        className="hv-cal-popover"
        ref={popoverRef}
        style={positionStyle}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          className="hv-cal-nav hv-cal-nav-prev"
          type="button"
          onClick={() => setCurrentMonth((prev) => addMonths(prev, -1))}
          aria-label="Previous month"
        >
          ‹
        </button>

        <button
          className="hv-cal-nav hv-cal-nav-next"
          type="button"
          onClick={() => setCurrentMonth((prev) => addMonths(prev, 1))}
          aria-label="Next month"
        >
          ›
        </button>

        <header className="hv-cal-header-strip">
          <button
            type="button"
            className={`hv-cal-pill ${activeField === "checkIn" ? "is-active" : ""}`}
            onClick={() => setActiveField("checkIn")}
          >
            {checkIn ? toIso(checkIn) : "Check-in"}
          </button>
          <span className="hv-cal-arrow">?</span>
          <button
            type="button"
            className={`hv-cal-pill ${activeField === "checkOut" ? "is-active" : ""}`}
            onClick={() => setActiveField("checkOut")}
          >
            {checkOut ? toIso(checkOut) : "Check-out"}
          </button>
          {checkIn && checkOut ? (
            <span className="hv-cal-nights">{totalNights} nights</span>
          ) : null}
        </header>

        <div className="hv-cal-months">
          {renderMonth(monthA, monthACells)}
          {renderMonth(monthB, monthBCells)}
        </div>

        <footer className="hv-cal-footer">
          <button
            type="button"
            className="hv-cal-clear"
            onClick={() => onChange?.({ checkIn: null, checkOut: null })}
          >
            Clear
          </button>
        </footer>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return content;
  }

  return createPortal(content, document.body);
}

